import type { CaveOptions, RackOptions } from "../../RackTypes"
import { findBundleProduct, handleSplitColorUprights } from "../../utils/rackbuilder-util"
import { CaveBuilder } from "./caveBuilder"
import { findCaveCableProduct } from "./utils/caveUtils"
import type { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { getBuyRequestMeta, getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import { getNamePlateMaterials, getTopCrossmemberMaterials, getCrownMaterials, parseRackDepth, getShelfSelections, getCommonCaveSelections, getCavePlateStorageSelection, getFxtSafetySelection, getDepthSelection, getFrontSelection, getBackSelection, getJCupSelection, getHeightSelection, getPanelWidthSelection, getFoldBackDepth, getCableSystemSelection } from "../../utils/selection-utils"

export function createCaveConfig(rogue: IRogue, buyRequest: BuyRequest): CaveOptions {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const rack = getSimpleForBuyRequest(buyRequest, rogue)
  
  const { color, stainlessUprights } = handleSplitColorUprights(rack?.attributes.rig_color as string)
  const txtGray = rack.attributes.rig_color?.toString().includes('Textured') && rack.attributes.rig_color?.toString().includes('Gray')
  const line = 'RM'
  const numbered = rack.attributes.edition === 'Numbered' || rack.attributes.upright_type === 'Numbered' || line === 'RM'

  // Materials
  const materials: RackOptions['materials'] = {}
  const namePlateMaterials = getNamePlateMaterials(rogue, buyRequest)
  const topCrossmemberMaterials = getTopCrossmemberMaterials(rogue, buyRequest)
  const crownMaterials = getCrownMaterials(rogue, buyRequest)
  
  if (crownMaterials) {
    materials.crown = crownMaterials
  }

  if (namePlateMaterials) {
      materials!.nameplate = namePlateMaterials
  }
  
  if (txtGray) {
      materials!.uprights = { main: { name: 'metal', color: '#5b5b59', material: 'powdercoat' } }
  }
  
  if (topCrossmemberMaterials) {
      materials!.topCrossmember = topCrossmemberMaterials
  }

  // Cable Systems
  const leftRackBR = buyRequest.children![1] // The left rack system is the second child in the grouped BR
  const leftCableSystem = findCaveCableProduct(rogue, leftRackBR, 'left cable', true)
  const rightRackBR = buyRequest.children![2] // The right rack system is the third child in the grouped BR
  const rightCableSystem = findCaveCableProduct(rogue, rightRackBR, 'right cable', true)

  const leftIndyDepth = parseRackDepth(leftCableSystem?.attributes?.rack_depth as string | undefined)
  const rightIndyDepth = parseRackDepth(rightCableSystem?.attributes?.rack_depth as string | undefined)

  // Shelf Selections
  const shelfConfig = {
    leftShelves  : getShelfSelections(rogue, leftRackBR),
    rightShelves : getShelfSelections(rogue, rightRackBR),
  }
  
  const { latBarSelection: leftLatBarAttachment, lowRowSelection: leftLowRowAttachment, trolleySelection: leftTrolleyAttachment } =  getCommonCaveSelections(rogue, leftRackBR, true, materials)
  const { latBarSelection: rightLatBarAttachment, lowRowSelection: rightLowRowAttachment, trolleySelection: rightTrolleyAttachment } =  getCommonCaveSelections(rogue, rightRackBR, false, materials)

  const addLeftSide = true 
  const addRightSide = true

  const mainRackType: CaveOptions['mainRackType'] = rack.name.toLowerCase().includes('fixed') ? 'fixed' : 'fold'
  const foldBackType: CaveOptions['foldBackType'] = String(rack.attributes.rack_size)?.toLowerCase()?.includes('4') ? '3w' : 'full'
  const is6Post = Boolean(mainRackType === 'fixed' && rack.attributes.rack_size?.toString().includes('6'))

  const leftRackSection = getSimpleForBuyRequest(leftRackBR, rogue)
  const leftShrouds =  Boolean(leftCableSystem?.attributes?.rig_or_wm === "Yes")
  const rightRackSection = getSimpleForBuyRequest(rightRackBR, rogue)
  const rightShrouds =  Boolean(rightCableSystem?.attributes?.rig_or_wm === "Yes")

  const isLeftSideAdded = leftRackBR.qty > 0
  const isRightSideAdded = rightRackBR.qty > 0

  const leftSeatSelection = findBundleProduct(rogue, leftRackBR, 'seat', true)?.name || ''
  const rightSeatSelection = findBundleProduct(rogue, rightRackBR, 'seat', true)?.name || ''

  const showEquipped = buyRequest.metadata?.showEquipped

  const leftPlateStorage = getCavePlateStorageSelection(rogue, leftRackBR) 
  const rightPlateStorage = getCavePlateStorageSelection(rogue, rightRackBR)

  return {
    color,
    materials,
    stainlessUprights,
    line,
    numbered,
    hardware         : 'isonite',
    safeties         : getFxtSafetySelection(rogue, buyRequest),
    depth            : getDepthSelection(rack, meta),
    front            : getFrontSelection(rogue, buyRequest, meta),
    back             : getBackSelection(rogue, buyRequest, meta),
    jcup             : getJCupSelection(rogue, buyRequest),
    height           : getHeightSelection(rack),
    leftPanelWidth   : getPanelWidthSelection(leftRackSection),
    rightPanelWidth  : getPanelWidthSelection(rightRackSection),
    foldBackDepth    : getFoldBackDepth(rack),
    leftCableSystem  : getCableSystemSelection(leftCableSystem),
    rightCableSystem : getCableSystemSelection(rightCableSystem),
    sixPost          : is6Post,
    foldBackType     : mainRackType === 'fold' ? foldBackType : undefined,
    isLeftSideAdded,
    isRightSideAdded,
    mainRackType,
    leftShrouds,
    rightShrouds,
    addLeftSide, 
    addRightSide,
    shelfConfig,
    leftSeatSelection,
    rightSeatSelection,
    leftLatBarAttachment,
    rightLatBarAttachment,
    leftTrolleyAttachment,
    rightTrolleyAttachment,
    leftLowRowAttachment,
    rightLowRowAttachment,
    showEquipped,
    leftIndyDepth,
    rightIndyDepth,
    leftPlateStorage,
    rightPlateStorage,
    accessories      : [],
  }
}

export function createCave(rogue: IRogue, buyRequest: BuyRequest) {
  const config = createCaveConfig(rogue, buyRequest)
  const part = new CaveBuilder(config).build() // No factory needed for this product
  
  return { config, part }
}